import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Plus, Trash2, CheckCircle, Circle, Clock, Minus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Todo } from '@/hooks/useTodos';

interface TodoItemProps {
  todo: Todo;
  toggleTodo: (id: string) => void;
  deleteTodo: (id: string) => void;
  getCountdown: (dueDate: number) => string;
}

const TodoItem = ({ todo, toggleTodo, deleteTodo, getCountdown }: TodoItemProps) => {
  const [isMarkingCompleted, setIsMarkingCompleted] = useState(false);
  const completionTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (completionTimerRef.current) clearTimeout(completionTimerRef.current);
    };
  }, []);

  const handleToggle = () => {
    if (todo.completed) {
        toggleTodo(todo.id);
    } else {
        setIsMarkingCompleted(true);
        completionTimerRef.current = setTimeout(() => {
            toggleTodo(todo.id);
            // No need to reset state as component will likely unmount/re-render due to filter
        }, 500);
    }
  };

  const isCompleted = todo.completed || isMarkingCompleted;

  return (
    <div
      className={`group flex items-center gap-2 p-3 rounded-xl transition-colors ${isCompleted ? 'bg-slate-50 dark:bg-white/5 opacity-50' : 'bg-slate-100 dark:bg-white/10 hover:bg-slate-200 dark:hover:bg-white/15'}`}
    >
      <button 
        onClick={handleToggle}
        className={`flex-shrink-0 ${isCompleted ? 'text-green-500 dark:text-green-400' : 'text-slate-400 hover:text-slate-600 dark:hover:text-white'}`}
      >
        {isCompleted ? <CheckCircle size={18} /> : <Circle size={18} />}
      </button>
      
      <div className="flex-1 min-w-0">
        <div className={`text-sm truncate ${isCompleted ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'}`}>
          {todo.text}
        </div>
        {todo.dueDate && !isCompleted && (
          <div className="flex items-center gap-1 text-[10px] text-orange-500 dark:text-orange-400 mt-1">
            <Clock size={10} />
            {getCountdown(todo.dueDate)}后到期
          </div>
        )}
      </div>
      
      <button 
        onClick={() => deleteTodo(todo.id)}
        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 dark:hover:text-red-400 transition-all"
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
};

interface TodoListProps {
  isExpanded?: boolean;
  onToggleExpand?: (expanded: boolean) => void;
  todoData: {
      todos: Todo[];
      addTodo: (text: string, dueDate?: string) => void;
      toggleTodo: (id: string) => void;
      deleteTodo: (id: string) => void;
      reorderTodos: (newTodos: Todo[]) => void;
  };
}

const TodoList = ({ isExpanded = true, onToggleExpand, todoData }: TodoListProps) => {
  const { todos, addTodo: addNewTodo, toggleTodo, deleteTodo } = todoData;
  const [newTodo, setNewTodo] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [dueDateInput, setDueDateInput] = useState('');

  const sortedTodos = useMemo(() => {
    // 1. Filter out completed todos
    const activeTodos = todos.filter(t => !t.completed);

    // 2. Group todos
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const tomorrow = today + 86400000;

    const overdue: Todo[] = [];
    const todayTodos: Todo[] = [];
    const tomorrowTodos: Todo[] = [];
    const upcoming: Todo[] = [];
    const noDate: Todo[] = [];

    activeTodos.forEach(todo => {
        if (!todo.dueDate) {
            noDate.push(todo);
            return;
        }

        const due = new Date(todo.dueDate).getTime();
        
        if (due < today) {
            overdue.push(todo);
        } else if (due >= today && due < tomorrow) {
            todayTodos.push(todo);
        } else if (due >= tomorrow && due < tomorrow + 86400000) {
            tomorrowTodos.push(todo);
        } else {
            upcoming.push(todo);
        }
    });

    // 3. Sort within groups (by due date ascending)
    const sortByDate = (a: Todo, b: Todo) => (a.dueDate || 0) - (b.dueDate || 0);
    
    overdue.sort(sortByDate);
    todayTodos.sort(sortByDate);
    tomorrowTodos.sort(sortByDate);
    upcoming.sort(sortByDate);
    // No date todos are kept in original order (or reverse if desired, user said "no time at the back", implied logic is fine)
    
    // 4. Concatenate in specific order: Overdue -> Today -> Tomorrow -> Upcoming -> NoDate
    return [
        ...overdue,
        ...todayTodos,
        ...tomorrowTodos,
        ...upcoming,
        ...noDate
    ];
  }, [todos]);

  const handleAddTodo = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTodo.trim()) return;

    addNewTodo(newTodo, dueDateInput);
    setNewTodo('');
    setDueDateInput('');
    setShowInput(false);
  };

  const getCountdown = (dueDate: number) => {
    const now = Date.now();
    const diff = dueDate - now;
    if (diff < 0) return '已过期';
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return `${days}天${hours}小时`;
  };

  if (!isExpanded) {
    return (
      <button 
        onClick={() => onToggleExpand?.(true)}
        className="bg-white/80 dark:bg-slate-900/50 backdrop-blur-md border border-slate-200 dark:border-white/10 rounded-full p-3 shadow-xl hover:scale-110 transition-transform text-blue-500 dark:text-blue-400 flex items-center justify-center absolute right-0 bottom-0"
        title="展开待办事项"
      >
        <div className="relative">
             <CheckCircle size={24} />
             {todos.filter(t => !t.completed).length > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] flex items-center justify-center rounded-full border border-white dark:border-slate-900">
                    {todos.filter(t => !t.completed).length}
                </span>
            )}
        </div>
      </button>
    );
  }

  return (
    <div className="w-full max-w-sm bg-white/80 dark:bg-slate-900/50 backdrop-blur-md border border-slate-200 dark:border-white/10 rounded-2xl p-4 shadow-xl">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-slate-800 dark:text-white font-medium flex items-center gap-2">
          <CheckCircle size={18} className="text-blue-500 dark:text-blue-400" />
          待办事项
        </h3>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowInput(!showInput)}
            className="text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors"
            title="添加任务"
          >
            <Plus size={20} />
          </button>
          {onToggleExpand && (
            <button 
              onClick={() => onToggleExpand(false)}
              className="text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white transition-colors"
              title="收起"
            >
              <Minus size={20} />
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showInput && (
          <motion.form 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            onSubmit={handleAddTodo}
            className="mb-4 overflow-hidden"
          >
            <input
              type="text"
              value={newTodo}
              onChange={(e) => setNewTodo(e.target.value)}
              placeholder="添加新任务..."
              className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-800 dark:text-white text-sm mb-2 outline-none focus:border-blue-500 dark:focus:border-blue-400"
              autoFocus
            />
             <input
              type="datetime-local"
              value={dueDateInput}
              onChange={(e) => setDueDateInput(e.target.value)}
              className="w-full bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 text-slate-600 dark:text-slate-300 text-xs mb-2 outline-none"
            />
            <button type="submit" className="w-full bg-blue-600 hover:bg-blue-500 text-white text-xs py-2 rounded-lg transition-colors">
              添加
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="max-h-[60vh] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-white/10">
        {sortedTodos.length === 0 && !showInput && (
          <div className="text-center text-slate-400 dark:text-slate-500 text-xs py-4">
             {todos.length > 0 ? '所有任务已完成！' : '暂无待办事项'}
          </div>
        )}
        
        <div className="space-y-2">
          {sortedTodos.map(todo => (
            <TodoItem
              key={todo.id}
              todo={todo}
              toggleTodo={toggleTodo}
              deleteTodo={deleteTodo}
              getCountdown={getCountdown}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default TodoList;
